/**
 * `<SettingsAuthRoute>` — `/w/:slug/settings/auth`.
 *
 * Consolidates the previous API Keys page + the new JWT Signing Keys
 * surface + personal MCP access tokens under one Authentication tab —
 * `?tab=api-keys` (default), `?tab=signing-keys`, or `?tab=mcp-access`.
 *
 * Per FD § 1.6 + NFR-Design § 3:
 *  - Non-admin operators see ONLY the API Keys tab (`useIsAdmin()` hides
 *    the Signing Keys tab, NFR-SEC-14 defense-in-depth).
 *  - Deep-linking a non-admin to `?tab=signing-keys` redirects them to
 *    `?tab=api-keys` with a one-time info toast.
 */

import { useAbility } from "@cometchat-ai/auth/client";
import { Button, toast } from "@cometchat-ai/ui-dashboard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { KeyRound } from "../components/icons";
import { McpAccessPanel } from "../components/mcp-access-panel";
import { PageShell } from "../components/page-shell";
import { SigningKeysList } from "../components/signing-keys/SigningKeysList";
import { useIsAdmin } from "../lib/auth/use-is-admin";
import { SettingsApiKeysRoute } from "./settings-api-keys";

type TabId = "api-keys" | "signing-keys" | "mcp-access";

export function SettingsAuthRoute() {
  const { t } = useTranslation("signing-keys");
  const { t: tApiKeys } = useTranslation("apikeys");
  const isAdmin = useIsAdmin();
  const ability = useAbility();
  const canCreateApiKey = ability.can("create", "ApiKey");
  const [apiKeyCreateOpen, setApiKeyCreateOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get("tab") as TabId | null;
  // If a non-admin lands on `?tab=signing-keys`, fall back to api-keys
  // and notify (one-time toast).
  const activeTab: TabId =
    requested === "mcp-access"
      ? "mcp-access"
      : requested === "signing-keys" && isAdmin
        ? "signing-keys"
        : "api-keys";

  useEffect(() => {
    if (requested === "signing-keys" && !isAdmin) {
      toast.info(t("adminOnlyToast"));
      setSearchParams({ tab: "api-keys" }, { replace: true });
    }
  }, [requested, isAdmin, setSearchParams, t]);

  const tabs: Array<{ value: TabId; label: string; testId: string }> = [
    { value: "api-keys", label: t("tabs.apiKeys"), testId: "settings-auth-tab-api-keys" },
    ...(isAdmin
      ? [
          {
            value: "signing-keys" as const,
            label: t("tabs.signingKeys"),
            testId: "settings-auth-tab-signing-keys",
          },
        ]
      : []),
    {
      value: "mcp-access" as const,
      label: t("tabs.mcpAccess"),
      testId: "settings-auth-tab-mcp-access",
    },
  ];

  return (
    <PageShell className="p-0" data-testid="settings-auth-route" description={t("pageSubtitle")}>
      <div className="flex items-center justify-between gap-2 px-[18px] py-[18px]">
        <div
          className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted p-0.5"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.value}
              data-active={activeTab === tab.value}
              data-testid={tab.testId}
              onClick={() => setSearchParams({ tab: tab.value })}
              className="inline-flex h-6 items-center rounded-[5px] px-2.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground data-[active=true]:bg-background data-[active=true]:text-foreground data-[active=true]:shadow-sm"
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === "api-keys" && canCreateApiKey ? (
          <Button
            size="sm"
            onClick={() => setApiKeyCreateOpen(true)}
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            data-testid="settings-api-keys-create"
          >
            <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
            {tApiKeys("create")}
          </Button>
        ) : null}
      </div>

      {activeTab === "mcp-access" ? (
        // MCP access: same centered form-style surface as signing keys.
        <div className="px-6 py-6">
          <McpAccessPanel />
        </div>
      ) : activeTab === "signing-keys" && isAdmin ? (
        // Signing keys: a centered form-style surface (current key card).
        <div className="px-6 py-6">
          <div className="mx-auto w-full max-w-6xl">
            <SigningKeysList />
          </div>
        </div>
      ) : (
        // API keys: an edge-to-edge Attio ruled table (matches Channels/Contacts).
        // The "Generate key" trigger lives up in the tab row above; the child
        // keeps its create-then-display dialog flow.
        <SettingsApiKeysRoute
          createOpen={apiKeyCreateOpen}
          onCreateOpenChange={setApiKeyCreateOpen}
          showToolbarButton={false}
        />
      )}
    </PageShell>
  );
}
